import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Search, Handshake, TrendingUp } from 'lucide-react';

const steps = [
  {
    icon: FileText,
    title: 'Crie seu perfil',
    desc: 'Conte quem você é, o que faz e por que seu negócio é especial. Leva poucos minutos.',
    color: 'blue',
    number: '01',
  },
  {
    icon: Search,
    title: 'Mostre sua ideia',
    desc: 'Apresente seu projeto com fotos, um pequeno vídeo e a história por trás dele.',
    color: 'gold',
    number: '02',
  },
  {
    icon: Handshake,
    title: 'Conecte-se com investidores',
    desc: 'Converse direto com pessoas que acreditam no seu negócio e querem fazer parte.',
    color: 'blue',
    number: '03',
  },
  {
    icon: TrendingUp,
    title: 'Nasce uma Spark',
    desc: 'O encontro entre ideia e investidor cria uma Spark — o começo de algo grande.',
    color: 'gold',
    number: '04',
  },
];

const HowItWorksSection = () => {
  return (
    <section id="how" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="Como funciona"
          title={<>Simples assim: <span className="gradient-text">4 passos</span> e pronto.</>}
          subtitle="Sem complicação, sem burocracia. Do cadastro ao primeiro investidor em poucos cliques."
        />

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              whileHover={{ y: -8 }}
              className="relative spotlight-card glass-card rounded-2xl p-6 card-hover group"
            >
              <div className={`absolute top-5 right-5 text-3xl heading-display font-bold opacity-20 ${
                step.color === 'gold' ? 'text-[#ffa726]' : 'text-[#4facfe]'
              }`}>
                {step.number}
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-all duration-500 ${
                step.color === 'gold'
                  ? 'bg-gradient-to-br from-[#ffa726]/20 to-[#ff7043]/10 text-[#ffb74d] group-hover:shadow-[0_0_30px_rgba(255,167,38,0.4)]'
                  : 'bg-gradient-to-br from-[#4facfe]/20 to-[#1e90ff]/10 text-[#4facfe] group-hover:shadow-[0_0_30px_rgba(79,172,254,0.4)]'
              }`}>
                <step.icon size={22} />
              </div>
              <h3 className="text-lg font-semibold text-white">{step.title}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{step.desc}</p>

              {/* Connector arrow between cards (desktop) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="w-6 h-6 rounded-full bg-[#050816] border border-white/10 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#4facfe] to-[#ffa726]" />
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const SectionHeader = ({ eyebrow, title, subtitle, align = 'center', gold }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.7 }}
      className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`}
    >
      <span className={`eyebrow ${gold ? 'eyebrow-gold' : ''}`}>{eyebrow}</span>
      <h2 className="mt-5 heading-display text-3xl sm:text-4xl lg:text-5xl text-white">{title}</h2>
      {subtitle && <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">{subtitle}</p>}
    </motion.div>
  );
};

export default HowItWorksSection;
